import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, X } from 'lucide-react'
import type { TemplateActiveUsage } from '../../types/artefact-templates'

interface ActiveDeploymentWarningDialogProps {
  isOpen: boolean
  usage: TemplateActiveUsage
  onProceed: () => void
  onCancel: () => void
}

export function ActiveDeploymentWarningDialog({
  isOpen,
  usage,
  onProceed,
  onCancel,
}: ActiveDeploymentWarningDialogProps): ReactElement | null {
  if (!isOpen) return null

  return <WarningContent usage={usage} onProceed={onProceed} onCancel={onCancel} />
}

interface WarningContentProps {
  usage: TemplateActiveUsage
  onProceed: () => void
  onCancel: () => void
}

function WarningContent({ usage, onProceed, onCancel }: WarningContentProps): ReactElement {
  const { t } = useTranslation('cases')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl dark:bg-slate-800">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h3 className="font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">
              {t('velociraptor.orgTemplates.activeWarningTitle')}
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label={t('velociraptor.orgTemplates.activeWarningCancel')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {t('velociraptor.orgTemplates.activeWarningMessage', {
              requestCount: usage.activeRequestCount,
              caseCount: usage.activeCaseCount,
            })}
          </p>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-700">
          <button
            onClick={onCancel}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            {t('velociraptor.orgTemplates.activeWarningCancel')}
          </button>
          <button
            onClick={onProceed}
            className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 focus:ring-2 focus:ring-amber-400 focus:outline-none"
          >
            {t('velociraptor.orgTemplates.activeWarningProceed')}
          </button>
        </div>
      </div>
    </div>
  )
}
